import { Inject, Injectable, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { LoggerModule, LoggerService } from '@wdpr/ra-angular-logger';
import { Observable as Observable$1 } from 'rxjs/Observable';
import extend from 'lodash-es/extend';

const FEATURE_TOGGLE_CONFIG = 'FEATURE_TOGGLE_CONFIG';

class FeatureToggleExtenderService {
    /**
     * @param {?} logger
     */
    constructor(logger) {
        this.logger = logger;
    }
    /**
     * @param {?} featureName
     * @param {?} toggles
     * @param {?} stateConfig
     * @param {?=} additionsIfFeatureEnabled
     * @param {?=} additionsIfFeatureDisabled
     * @return {?}
     */
    extendStateConfig(featureName, toggles, stateConfig, additionsIfFeatureEnabled, additionsIfFeatureDisabled) {
        const /** @type {?} */ toggleStatus = toggles[featureName];
        if (toggleStatus && additionsIfFeatureEnabled) {
            extend(stateConfig, additionsIfFeatureEnabled);
        }
        else if (!toggleStatus && additionsIfFeatureDisabled) {
            extend(stateConfig, additionsIfFeatureDisabled);
        }
        else {
            this.logger.error(`extendStateConfig - ${toggles}[${featureName}] is not a boolean or additions to the config was not supplied`);
        }
        if (stateConfig && additionsIfFeatureDisabled) {
            this.logger.error('extendStateConfig - featureName, Toggles Map or state was not supplied');
            extend(stateConfig, additionsIfFeatureEnabled);
        }
        return stateConfig;
    }
    /**
     * @param {?} featureName
     * @param {?} toggles
     * @param {?} statesArray
     * @param {?} stateToBeAdded
     * @return {?}
     */
    addState(featureName, toggles, statesArray, stateToBeAdded) {
        const /** @type {?} */ toggleStatus = toggles[featureName];
        if (toggleStatus && statesArray && stateToBeAdded) {
            statesArray.push(stateToBeAdded);
        }
        else {
            this.logger.error(`addState - ${toggles}[${featureName}] is not a boolean or config
                             to be added or the state array was not supplied.`);
        }
    }
}
FeatureToggleExtenderService.decorators = [
    { type: Injectable },
];
/**
 * @nocollapse
 */
FeatureToggleExtenderService.ctorParameters = () => [
    { type: LoggerService, },
];

class FeatureToggleService {
    /**
     * @param {?} config
     * @param {?} featureToggleExtender
     * @param {?} http
     * @param {?} logger
     */
    constructor(config, featureToggleExtender, http$$1, logger) {
        this.config = config;
        this.featureToggleExtender = featureToggleExtender;
        this.http = http$$1;
        this.logger = logger;
        this.featureToggleUrl = '/api/v1/toggles';
        this.appTogglesName = 'appToggles';
        this.featureToggleUrl = config.featureToggleUrl || this.featureToggleUrl;
        this.appTogglesName = config.appTogglesName || this.appTogglesName;
    }
    /**
     * @param {?} featureSet
     * @param {?=} iterator
     * @return {?}
     */
    getToggles(featureSet, iterator) {
        return Observable$1.create(subscriber => {
            this.getFeatures(featureSet)
                .subscribe(features => this.addContext(featureSet, features, subscriber, iterator), error => this.rejectToggleRequest(error, subscriber));
        });
    }
    /**
     * @param {?} featureSet
     * @return {?}
     */
    getFeatures(featureSet) {
        const /** @type {?} */ url = `${this.featureToggleUrl}/${featureSet}`;
        return Observable$1.create(subscriber => {
            this.http.get(url, {
                withCredentials: true
            })
                .subscribe(data => subscriber.next(data), error => this.retrieveFeatureError(error, subscriber));
        });
    }
    /**
     * @param {?} error
     * @param {?} subscriber
     * @return {?}
     */
    retrieveFeatureError(error, subscriber) {
        this.logger.error(`'Internal Error- Get Features,  Error Details:' ${error.message}`);
        subscriber.error(error);
    }
    /**
     * @param {?} error
     * @param {?} subscriber
     * @return {?}
     */
    rejectToggleRequest(error, subscriber) {
        this.logger.error(error.message);
        subscriber.error(error.message);
    }
    /**
     * @param {?} featureSet
     * @param {?} features
     * @param {?} subscriber
     * @param {?=} iterator
     * @return {?}
     */
    addContext(featureSet, features, subscriber, iterator) {
        // Fills the context for each feature based on the iteration function provided
        if (iterator) {
            features.forEach(feature => iterator(feature));
        }
        else {
            this.logger.log('No context attached for features');
        }
        this.getFeatureToggles(featureSet, features, subscriber);
    }
    /**
     * @param {?} featureSet
     * @param {?} featuresWithContext
     * @param {?} subscriber
     * @return {?}
     */
    getFeatureToggles(featureSet, featuresWithContext, subscriber) {
        const /** @type {?} */ url = `${this.featureToggleUrl}/${featureSet}`;
        this.http.post(url, featuresWithContext, {
            withCredentials: true
        }).subscribe(data => subscriber.next(data), error => this.rejectToggleRequest(error, subscriber));
    }
    /**
     * @param {?} featureName
     * @param {?} statesArray
     * @param {?} statesToBeAdded
     * @return {?}
     */
    addState(featureName, statesArray, statesToBeAdded) {
        this.featureToggleExtender.addState(featureName, this.appTogglesName, statesArray, statesToBeAdded);
    }
    /**
     * @param {?} featureName
     * @param {?} stateConfig
     * @param {?} additionsIfFeatureEnabled
     * @param {?} additionsIfFeatureDisabled
     * @return {?}
     */
    extendStateConfig(featureName, stateConfig, additionsIfFeatureEnabled, additionsIfFeatureDisabled) {
        this.featureToggleExtender.extendStateConfig(featureName, this.appTogglesName, stateConfig, additionsIfFeatureEnabled, additionsIfFeatureDisabled);
    }
    /**
     * @return {?}
     */
    getPrefetchedToggles() {
        return this.appTogglesName;
    }
}
FeatureToggleService.decorators = [
    { type: Injectable },
];
/**
 * @nocollapse
 */
FeatureToggleService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [FEATURE_TOGGLE_CONFIG,] },] },
    { type: FeatureToggleExtenderService, },
    { type: HttpClient, },
    { type: LoggerService, },
];

class FeatureToggleModule {
}
FeatureToggleModule.decorators = [
    { type: NgModule, args: [{
                imports: [
                    CommonModule,
                    HttpClientModule,
                    LoggerModule
                ],
                declarations: [],
                providers: [FeatureToggleService, FeatureToggleExtenderService],
                exports: []
            },] },
];
/**
 * @nocollapse
 */
FeatureToggleModule.ctorParameters = () => [];

// Anything publicly available should be listed here

/**
 * Generated bundle index. Do not edit.
 */

export { FeatureToggleModule, FEATURE_TOGGLE_CONFIG, FeatureToggleService, FeatureToggleExtenderService };
//# sourceMappingURL=ra-angular-feature-toggle.js.map
